// =============================================================================
// E7 FRONT DOOR — the 7 card bodies + shared chrome primitives (LANE
// E7-PORT-THROUGHLINE, docs/diagrams/c4-e7-front-door-2026-07-17.html). Ported
// from apps/web/src/components/onboarding/{cards.tsx,OnboardingShell.tsx}'s
// SuggestionBadge/SkippedBadge/CardActionRow + the 7 PascalCase*Card
// components — same meaning, copy, and prop shapes (value/suggestion/saving/
// onSave), restyled for throughline's CSS-Modules + --tl-* token system
// instead of Tailwind. Each is self-contained and exported so it renders
// identically whether driven by the live flow (FrontDoorCards.tsx) or the
// StyleGuide matrix (mock props, no backend — rule 5). Every text input uses
// this app's existing VoiceTextarea (mic on every field, rule 2) — single-
// line fields use rows={1}, the SAME idiom RoleEditForm.tsx/
// AchievementEditForm.tsx already establish (this app has no dedicated
// single-line VoiceInput; VoiceTextarea rows={1} IS that idiom here).
//
// Card 7 (the finale) is the one deliberate layout departure from the
// reference: apps/web's FinaleCard offers a "choice" screen (two big
// upload/paste buttons) before revealing either door. throughline ALREADY
// has a single combined upload+paste door — ImportPasteCard.tsx (the SAME
// component the existing corpus-discovery arc's Refine tab and its own
// one-question onboarding step use) — so the finale here composes THAT
// component directly rather than rebuilding a parallel chooser. This is the
// literal meaning of "the finale card's upload/paste routes into the arc's
// own corpus entry": same functions (importCorpusText/importCorpusFile),
// same component, same honest result copy (describeResult) — not a second
// upload surface with its own drift risk. See FrontDoorCards.tsx's own doc
// comment for how the resulting import is threaded back into the existing
// arc's corpus-truth gate.
// =============================================================================

import { useState, type ReactNode } from 'react';
import { COUNTRIES, countryName } from '../lib/countries';
import {
  RADIUS_CHIPS_MILES,
  RELOCATION_POSTURES,
  selectWorkRightsNone,
  toggleInArray,
  toggleWorkRightsCountry,
  type RelocationPosture,
} from '../lib/frontDoorCards';
import { ImportPasteCard } from './ImportPasteCard';
import type { ImportResult } from '../lib/api';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './FrontDoorCards.module.css';

// ── Shared chrome primitives ─────────────────────────────────────────────

/** The suggestion badge — visually distinct from a confirmed value (NO
 *  INVENTION: suggestion != decision), shown until the user's own tap
 *  confirms it. Used by card 1 (preferred name) and card 2 (home country). */
export function SuggestionBadge({ children }: { children: ReactNode }) {
  return <span className={styles.suggestionBadge}>Suggested: {children}</span>;
}

/** The skipped indicator — shown when the user backs into a card they
 *  previously skipped (still empty, never guessed), so re-visiting reads as
 *  "pick up where you left off" rather than an indistinguishable blank idle
 *  card. Neutral-toned (never the accent/suggestion color — a skip isn't a
 *  proposal, it's an honest "not answered yet"). */
export function SkippedBadge() {
  return <span className={styles.skippedBadge}>Skipped — answer anytime</span>;
}

/**
 * The per-card chrome — title, optional subtitle, the skipped badge, the
 * card body (children), and the "skip this question" text link. Mirrors
 * apps/web's OnboardingShell scope MINUS the progress dots and the global
 * skip button — those live once, at the overlay level, in
 * FrontDoorCards.tsx's own render, matching THIS app's existing Onboarding.
 * tsx convention (a single topRow with dots+Skip, not a per-step chrome
 * component) rather than re-importing apps/web's own factoring wholesale.
 * Exported so the StyleGuide preview renders each card inside the EXACT same
 * chrome a real user sees, never an approximation.
 */
export function StepFrame({
  title,
  subtitle,
  skippedBadge,
  onSkip,
  skipLabel = 'Skip this question',
  children,
}: {
  title: string;
  subtitle?: string;
  /** True when this card was previously skipped THIS session and still has
   *  no confirmed value — see FrontDoorCards.tsx's own skippedSteps set. */
  skippedBadge?: boolean;
  onSkip?: () => void;
  skipLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.stepColumn}>
      <div className={styles.cardTitle}>{title}</div>
      {subtitle ? <div className={styles.cardSubtitle}>{subtitle}</div> : null}
      {skippedBadge ? (
        <div style={{ marginBottom: 10 }}>
          <SkippedBadge />
        </div>
      ) : null}
      {children}
      {onSkip ? (
        <button type="button" className={styles.textSkipButton} onClick={onSkip}>
          {skipLabel}
        </button>
      ) : null}
    </div>
  );
}

/** The consistent primary-action button every card ends its own content
 *  with — label/enabled-state is the card's own decision, plus the shared
 *  saving spinner (mirrors apps/web's CardActionRow). */
export function CardButton({
  label,
  onClick,
  disabled,
  saving,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  saving?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      onClick={onClick}
      disabled={disabled || saving}
    >
      {saving ? <span className={styles.spinner} aria-hidden="true" /> : null}
      {saving ? 'Saving…' : label}
    </button>
  );
}

// ── Card 1 — preferred name ──────────────────────────────────────────────

export function PreferredNameCard({
  value,
  suggestion,
  saving,
  onSave,
}: {
  value: string | null;
  suggestion: string | null;
  saving?: boolean;
  onSave: (name: string) => void;
}) {
  const [text, setText] = useState(value ?? suggestion ?? '');
  const showSuggestion = !value && !!suggestion;

  return (
    <div>
      {showSuggestion ? <SuggestionBadge>{suggestion}</SuggestionBadge> : null}
      <VoiceTextarea
        label="Preferred name"
        value={text}
        onChange={setText}
        placeholder="What should Meira call you?"
        rows={1}
        disabled={saving}
      />
      <CardButton
        label={text.trim() ? `Continue as "${text.trim()}"` : 'Continue'}
        disabled={!text.trim()}
        saving={saving}
        onClick={() => onSave(text.trim())}
      />
    </div>
  );
}

// ── Card 2 — home country (confirm) ──────────────────────────────────────

export function HomeCountryCard({
  value,
  suggestion,
  saving,
  onSave,
}: {
  value: string | null;
  suggestion: string | null;
  saving?: boolean;
  onSave: (code: string) => void;
}) {
  const [code, setCode] = useState(value ?? suggestion ?? '');
  const showSuggestion = !value && !!suggestion;
  const label = countryName(code);

  return (
    <div>
      {showSuggestion ? <SuggestionBadge>{countryName(suggestion) ?? suggestion}</SuggestionBadge> : null}
      <label className={styles.fieldLabel} htmlFor="fd-home-country">
        Home country
      </label>
      <select
        id="fd-home-country"
        className={styles.countrySelect}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={saving}
      >
        <option value="" disabled>
          Choose a country…
        </option>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>
      <CardButton
        label={label ? `Confirm ${label}` : 'Confirm'}
        disabled={!code}
        saving={saving}
        onClick={() => onSave(code)}
      />
    </div>
  );
}

// ── Card 3 — post code + radius ──────────────────────────────────────────

export function PostalRadiusCard({
  postalCode,
  radiusMiles,
  saving,
  onSave,
}: {
  postalCode: string | null;
  radiusMiles: number | null;
  saving?: boolean;
  onSave: (patch: { postalCode: string; radiusMiles: number | null }) => void;
}) {
  const [code, setCode] = useState(postalCode ?? '');
  const [radius, setRadius] = useState<number | null>(radiusMiles ?? null);

  return (
    <div>
      <VoiceTextarea
        label="Post code"
        value={code}
        onChange={setCode}
        placeholder="e.g. 33139"
        hint="never a full address"
        rows={1}
        disabled={saving}
      />
      <div className={styles.chipRow}>
        {RADIUS_CHIPS_MILES.map((mi) => (
          <button
            key={mi}
            type="button"
            onClick={() => setRadius(mi)}
            disabled={saving}
            className={`${styles.chip} ${radius === mi ? styles.chipSelected : ''}`}
          >
            {mi} mi
          </button>
        ))}
      </div>
      <CardButton
        label="Continue"
        disabled={!code.trim()}
        saving={saving}
        onClick={() => onSave({ postalCode: code.trim(), radiusMiles: radius })}
      />
    </div>
  );
}

// ── Card 4 — work rights elsewhere ───────────────────────────────────────
//
// GUARD (design doc's own documented trap, preserved verbatim): Continue
// stays DISABLED at zero selections when NEITHER a country NOR "No others"
// is picked — an empty-array save would poison the citizenship-derived
// default via overrides (PUT /profile marks ANY present field as a user
// override, even an empty array; a 0-selection "save" would permanently
// lock out the server's own citizenship-derived work-rights default for a
// user who simply has nothing to declare here).
//
// E7-WALK-WAVE W2 (his live test, screenshot-annotated "Needs a no other
// option upfront", 2026-07-17): the zero-selection dead-end above had NO
// honest "none" answer — the ONLY way out used to be Skip, which reads as
// "unanswered," not "answered: no others." The "No others — home country
// only" chip is that missing honest answer, persisted DISTINCTLY (never as
// workRights: []) — see lib/frontDoorCards.ts's buildWorkRightsPatch for the
// exact guard, and profile.routes.ts's UpdateSchema doc comment on
// workRightsNone for why an empty array specifically is the poison. Mutual
// exclusion (toggleWorkRightsCountry / selectWorkRightsNone, both pure and
// unit-tested) — the two answers can never coexist client-side.

export function WorkRightsCard({
  selected,
  noneDeclared,
  saving,
  onSave,
}: {
  selected: string[];
  /** W2 — attributes.workRightsNone, already resolved against a possibly-
   *  stale `selected` by lib/frontDoorCards.ts's resolveWorkRightsSelection
   *  (the caller's job — this card just renders what it's handed). */
  noneDeclared: boolean;
  saving?: boolean;
  onSave: (answer: { codes: string[]; none: boolean }) => void;
}) {
  const [picked, setPicked] = useState<string[]>(selected);
  const [none, setNone] = useState<boolean>(noneDeclared);

  const canContinue = none || picked.length > 0;

  return (
    <div>
      <p className={styles.multiSelectBody}>
        Beyond your home country, where else do you hold the right to work? Select any that apply
        — this is your own declaration, never guessed.
      </p>
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          const next = selectWorkRightsNone();
          setPicked(next.picked);
          setNone(next.none);
        }}
        className={`${styles.noneChip} ${none ? styles.noneChipSelected : ''}`}
        aria-pressed={none}
      >
        No others — home country only
      </button>
      <div className={styles.multiSelectGrid}>
        {COUNTRIES.map((c) => {
          const on = picked.includes(c.code);
          return (
            <button
              key={c.code}
              type="button"
              disabled={saving}
              onClick={() => {
                const next = toggleWorkRightsCountry({ picked, none }, c.code);
                setPicked(next.picked);
                setNone(next.none);
              }}
              className={`${styles.countryChip} ${on ? styles.countryChipSelected : ''}`}
              aria-pressed={on}
            >
              {c.name}
            </button>
          );
        })}
      </div>
      <CardButton
        label={
          none
            ? 'Continue — no other work rights'
            : picked.length
              ? `Continue with ${picked.length} selected`
              : 'Select at least one, or "No others"'
        }
        disabled={!canContinue}
        saving={saving}
        onClick={() => onSave({ codes: picked, none })}
      />
    </div>
  );
}

// ── Card 5 — open to relocation ──────────────────────────────────────────
//
// E7-WALK-WAVE W1 (his live 3-screenshot test, 2026-07-17): this was a
// single-choice pick (stay local OR domestic OR international) — his
// requirement is that these coexist, since a real person is routinely local
// AND open domestically AND interested internationally all at once. Now a
// MULTI-select, same visual language as card 4's work-rights chips
// (multiSelectGrid/countryChip/countryChipSelected, reused verbatim rather
// than duplicated) instead of the old single-select block-button grid.
// Persists as attributes.relocationPostures (an array) — see
// lib/frontDoorCards.ts's resolveRelocationPostures for the additive,
// never-drop-the-old-key read-side shape.

export function RelocationCard({
  postures,
  targets,
  saving,
  onSave,
}: {
  postures: RelocationPosture[];
  targets: string[];
  saving?: boolean;
  onSave: (patch: { postures: RelocationPosture[]; targets: string[] }) => void;
}) {
  const [selectedPostures, setSelectedPostures] = useState<RelocationPosture[]>(postures);
  const [targetText, setTargetText] = useState(targets.join(', '));
  const needsTargets =
    selectedPostures.includes('domestic') || selectedPostures.includes('international');

  return (
    <div>
      <p className={styles.multiSelectBody}>
        These can all be true at once — select every posture that applies, not just one.
      </p>
      <div className={styles.multiSelectGrid}>
        {RELOCATION_POSTURES.map((p) => {
          const on = selectedPostures.includes(p.value);
          return (
            <button
              key={p.value}
              type="button"
              disabled={saving}
              onClick={() => setSelectedPostures((prev) => toggleInArray(prev, p.value))}
              className={`${styles.countryChip} ${on ? styles.countryChipSelected : ''}`}
              aria-pressed={on}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {needsTargets ? (
        <div className={styles.targetsField}>
          <VoiceTextarea
            label="Where — cities, states, or countries"
            value={targetText}
            onChange={setTargetText}
            placeholder="e.g. Austin, Denver, or Canada"
            rows={2}
            hint="comma-separated"
            disabled={saving}
          />
        </div>
      ) : null}
      <CardButton
        label={selectedPostures.length ? 'Continue' : 'Select at least one to continue'}
        disabled={selectedPostures.length === 0}
        saving={saving}
        onClick={() =>
          onSave({
            postures: selectedPostures,
            targets: needsTargets
              ? targetText
                  .split(/[,\n]/)
                  .map((t) => t.trim())
                  .filter(Boolean)
              : [],
          })
        }
      />
    </div>
  );
}

// ── Card 6 — email (read-only) + optional phone ──────────────────────────

export function ContactCard({
  email,
  phone,
  saving,
  onSave,
}: {
  email: string;
  phone: string | null;
  saving?: boolean;
  onSave: (phone: string | null) => void;
}) {
  const [phoneText, setPhoneText] = useState(phone ?? '');
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be denied/unavailable — the email is still selectable
      // text on the page, so this is a silent no-op, never an error state.
    }
  };

  return (
    <div>
      <label className={styles.fieldLabel}>Your sign-in email</label>
      <div className={styles.emailRow}>
        <span className={styles.emailValue}>{email}</span>
        <button type="button" className={styles.copyButton} onClick={copyEmail} aria-label="Copy email">
          {copied ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={styles.copyButtonCopied}>
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="6" y="6" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M4 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          )}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className={styles.fieldHint}>
        This can’t be changed here — features like emailing Meira work from this address.
      </p>
      <div className={styles.phoneField}>
        <VoiceTextarea
          label="Phone (optional)"
          value={phoneText}
          onChange={setPhoneText}
          placeholder="For a future text feature — not used yet"
          rows={1}
          disabled={saving}
        />
      </div>
      <CardButton label="Continue" saving={saving} onClick={() => onSave(phoneText.trim() || null)} />
    </div>
  );
}

// ── Card 7 — the finale: seed the corpus ─────────────────────────────────

export function FinaleCard({
  seeded,
  resultNote,
  saving,
  onImported,
  onFinish,
}: {
  /** Set by the PARENT once a real import actually succeeds (a server round
   *  trip) — the only thing that can flip this view, mirrors apps/web's own
   *  FinaleCard "seeded" prop discipline (never an internal state change). */
  seeded: boolean;
  /** The honest, count-only summary of what the server found (ImportPaste-
   *  Card's own describeResult) — shown once seeded, never a guessed count. */
  resultNote?: string | null;
  saving?: boolean;
  onImported: (result: ImportResult) => void;
  onFinish: () => void;
}) {
  if (seeded) {
    return (
      <div className={styles.finaleSeededWrap}>
        <div className={styles.finaleCheck}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className={styles.finalePromise}>
          Got it — we’re going to talk through it and pull it all together.
        </div>
        {resultNote ? <div className={styles.finaleResultNote}>{resultNote}</div> : null}
        <CardButton label="Let’s go" saving={saving} onClick={onFinish} />
      </div>
    );
  }

  return (
    <div>
      <p className={styles.finaleIntro}>
        Last step — upload or paste your résumé, CV, or LinkedIn profile. We’re going to talk
        through it and pull it all together.
      </p>
      {/* E7-WALK-WAVE W4 (his live test — pasting a long LinkedIn profile on
          mobile is nearly impossible, 2026-07-17): opts THIS usage into a
          visibly larger paste target + reassurance copy via ImportPasteCard's
          two new additive props (largeTarget/helperCopy) — the Refine tab's
          own call site is untouched, so its sizing/copy stays exactly as
          before. "you can add more after" is a TRUE claim, not just
          reassuring copy: runCorpusImport (apps/api/routes/corpus.routes.ts)
          fuzzy-matches every import against the user's EXISTING roles and
          only ever inserts what's new — sequential imports merge/append,
          they never replace. */}
      <ImportPasteCard
        onImported={onImported}
        helperCopy="Paste as much or as little as you like — you can add more after."
        largeTarget
      />
    </div>
  );
}
